import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { Dropdown, DropDownButton } from '@/shared/components/Dropdown';
import { listAvailableSubscriptions } from '../api';
import { viewSubscriptionPlan } from '@/epics/subscriptionManagment/api/viewSubscriptionPlanApiService';
import type { MenuItemProps } from '@/shared/components/Menu/types';

export interface SubscriptionPlanDropdownProps {
  /** Currently selected plan IDs */
  selectedIds: string[];
  /** Called when selection changes (ids array) */
  onSelectionChange: (ids: string[]) => void;
  /** Trigger button label (e.g. "Plan subskrypcji") */
  label: ReactNode;
  /** Disable the dropdown */
  disabled?: boolean;
  /** Error state */
  error?: boolean;
  /** Error message below trigger */
  errorMessage?: string;
  /** When true, show tooltip for selected items. */
  toolTip?: boolean;
  /**
   * Called once after the first page loads.
   * Receives `true` if there are any available plans, `false` if none exist.
   */
  onHasItems?: (hasItems: boolean) => void;
  /**
   * Cap the visible item count. When set, uses a fixed item-count limit instead of
   * the dynamic autoMaxHeight boundary detection (use this outside FilterPopup contexts).
   */
  maxVisibleItems?: number;
}

const SubscriptionPlanDropdownComponent = function SubscriptionPlanDropdown({
  selectedIds,
  onSelectionChange,
  maxVisibleItems,
  label,
  disabled = false,
  error = false,
  errorMessage,
  toolTip,
  onHasItems,
}: SubscriptionPlanDropdownProps) {
  const [plans, setPlans] = useState<{ id: string; label: string }[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  // Resolved names for selected IDs not yet in the paginated plans list (e.g. edit mode, page 2+)
  const [resolvedNames, setResolvedNames] = useState<Record<string, string>>(
    {}
  );
  const resolvingRef = useRef<Set<string>>(new Set());

  // Initial load
  useEffect(() => {
    let mounted = true;
    const fetchFirstPage = async () => {
      setLoading(true);
      try {
        const result = await listAvailableSubscriptions({ page: 1, limit: 10 });
        if (mounted) {
          const items = result.data.map((plan) => ({
            id: plan.id,
            label: plan.planName,
          }));
          setPlans(items);
          setHasMore(result.hasNextPage);
          setPage(1);
          onHasItems?.(result.totalCount > 0);
        }
      } catch (err) {
        console.error('Error fetching subscription plans:', err);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };
    fetchFirstPage();
    return () => {
      mounted = false;
    };
    // onHasItems intentionally omitted — only called once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMore = useCallback(async () => {
    if (loading || !hasMore) {
      return;
    }
    setLoading(true);
    const nextPage = page + 1;
    try {
      const result = await listAvailableSubscriptions({
        page: nextPage,
        limit: 10,
      });
      setPlans((prev) => {
        const newItems = result.data
          .map((plan) => ({ id: plan.id, label: plan.planName }))
          .filter((newItem) => !prev.some((item) => item.id === newItem.id));
        return [...prev, ...newItems];
      });
      setHasMore(result.hasNextPage);
      setPage(result.page);
    } catch (err) {
      console.error('Error fetching more subscription plans:', err);
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, page]);

  // Resolve display names for selected IDs not present in the loaded plans list.
  // Handles edit mode (selectedIds pre-populated before plans load) and pagination misses.
  useEffect(() => {
    const unresolvedIds = selectedIds.filter(
      (id) =>
        !plans.find((p) => p.id === id) &&
        !resolvedNames[id] &&
        !resolvingRef.current.has(id)
    );
    if (unresolvedIds.length === 0) {
      return;
    }

    unresolvedIds.forEach(async (id) => {
      resolvingRef.current.add(id);
      const result = await viewSubscriptionPlan({ planId: id });
      if (result.success && result.data?.planName) {
        const planName = result.data.planName;
        setResolvedNames((prev) => ({ ...prev, [id]: planName }));
      }
      resolvingRef.current.delete(id);
    });
    // resolvedNames intentionally omitted — resolvingRef gates duplicate fetches
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, plans]);

  const items: MenuItemProps[] = plans.map((plan) => ({
    id: plan.id,
    label: plan.label,
  }));

  if (loading) {
    items.push({
      id: '__loading__',
      label: 'Ładowanie...',
      disabled: true,
      showCheckbox: false,
    });
  }

  return (
    <Dropdown
      menuFullWidth
      {...(maxVisibleItems !== undefined
        ? { menuMaxVisibleItems: maxVisibleItems }
        : { autoMaxHeight: true })}
      items={items}
      selectedId={selectedIds}
      error={error}
      {...(errorMessage && { errorMessage })}
      showSearch={true}
      showCheckboxes={true}
      multiSelect={true}
      toolTip={toolTip}
      trigger={({
        open,
        toggle,
        disabled: triggerDisabled,
        readOnly,
        error: triggerError,
        toolTip: triggerToolTip,
      }) => (
        <DropDownButton
          label={label}
          value={selectedIds.map((id) => {
            const plan = plans.find((p) => p.id === id);
            return plan ? plan.label : (resolvedNames[id] ?? id);
          })}
          open={open}
          toggle={toggle}
          disabled={triggerDisabled}
          readOnly={readOnly ?? false}
          error={triggerError}
          toolTip={triggerToolTip}
        />
      )}
      onItemClick={(id) => {
        if (id !== '__loading__') {
          onSelectionChange(
            selectedIds.includes(id)
              ? selectedIds.filter((p) => p !== id)
              : [...selectedIds, id]
          );
        }
      }}
      disabled={disabled}
      onScrollEnd={() => {
        if (hasMore && !loading) {
          fetchMore();
        }
      }}
    />
  );
};

export const SubscriptionPlanDropdown = React.memo(
  SubscriptionPlanDropdownComponent
);
